/**
 * Calculation and formatting functions for ClientInfoCard
 */

import { formatDistanceToNow } from 'date-fns';
import logger from '@/lib/logging';
import type { ClassificationInfo } from './types';

/**
 * Format a date for display using relative time
 * Returns "None" if date is null or invalid
 */
export function formatDateForDisplay(date: Date | null | undefined): string {
  // Early return for null/undefined
  if (!date) return 'None';
  
  // Check if date is a valid Date object
  if (!(date instanceof Date)) {
    logger.warn('formatDateForDisplay: date is not a Date object', { date, type: typeof date });
    return 'Invalid date';
  }
  
  // Check if the date is valid (not NaN)
  const timestamp = date.getTime();
  if (isNaN(timestamp) || !isFinite(timestamp)) {
    logger.warn('formatDateForDisplay: invalid date value', { date, timestamp });
    return 'Invalid date';
  }
  
  // Additional validation: check if date is within reasonable bounds
  // Dates before year 1000 or after year 10000 are likely invalid
  const year = date.getFullYear();
  if (year < 1000 || year > 10000) {
    logger.warn('formatDateForDisplay: date out of reasonable range', { date, year });
    return 'Invalid date';
  }
  
  try {
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    logger.error('Error formatting date:', error);
    return 'Unknown';
  }
}

/**
 * Format classification information for display
 * Format: "Posture: X | SOP: Y" or individual classifications
 */
export function formatClassificationDisplay(classificationInfo: ClassificationInfo): string {
  const { posture, sop } = classificationInfo;
  
  if (posture && sop) {
    return `Posture: ${posture} | SOP: ${sop}`;
  }
  
  if (posture) {
    return `Posture: ${posture}`;
  }
  
  if (sop) {
    return `SOP: ${sop}`;
  }
  
  return 'No classification available';
}

/**
 * Calculate days since a date
 * Returns null if date is null
 */
export function calculateDaysSince(date: Date | null): number | null {
  if (!date) return null;
  try {
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  } catch (error) {
    logger.error('Error calculating days since:', error);
    return null;
  }
}

